const SESSION_STORAGE_KEY_PREFIX = 'museflow_loop_position_';

interface LastLoopPosition {
  songId: string;
  startTimestamp: number;
  endTimestamp: number;
}

function buildStorageKey(songId: string): string {
  return `${SESSION_STORAGE_KEY_PREFIX}${songId}`;
}

function isValidString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

function parseStoredPosition(raw: string): LastLoopPosition | null {
  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    const record = parsed as Record<string, unknown>;

    const hasSongId = isValidString(record.songId);
    const hasStartTimestamp = isValidNumber(record.startTimestamp);
    const hasEndTimestamp = isValidNumber(record.endTimestamp);

    if (!hasSongId || !hasStartTimestamp || !hasEndTimestamp) {
      return null;
    }

    return {
      songId: record.songId as string,
      startTimestamp: record.startTimestamp as number,
      endTimestamp: record.endTimestamp as number,
    };
  } catch {
    return null;
  }
}

function getLastLoopPosition(songId: string): LastLoopPosition | null {
  if (!isValidString(songId)) {
    return null;
  }

  const storageKey = buildStorageKey(songId);
  const storedValue = sessionStorage.getItem(storageKey);

  if (storedValue === null) {
    return null;
  }

  return parseStoredPosition(storedValue);
}

function setLastLoopPosition(songId: string, startTimestamp: number, endTimestamp: number): void {
  if (!isValidString(songId)) {
    throw new TypeError('songId must be a non-empty string');
  }

  if (!isValidNumber(startTimestamp)) {
    throw new TypeError('startTimestamp must be a valid number');
  }

  if (!isValidNumber(endTimestamp)) {
    throw new TypeError('endTimestamp must be a valid number');
  }

  const storageKey = buildStorageKey(songId);
  const position: LastLoopPosition = { songId, startTimestamp, endTimestamp };

  sessionStorage.setItem(storageKey, JSON.stringify(position));
}

function clearLastLoopPosition(songId: string): void {
  if (!isValidString(songId)) {
    throw new TypeError('songId must be a non-empty string');
  }

  const storageKey = buildStorageKey(songId);
  sessionStorage.removeItem(storageKey);
}

export type { LastLoopPosition };
export { getLastLoopPosition, setLastLoopPosition, clearLastLoopPosition };
